import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Divider,
  List,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  fetchClassById,
  fetchTestSubmission,
  fetchTestById,
  fetchCourseRoster,
} from "../../api/backend";

const DS = {
  surface: "var(--color-surface-2)",
  border: "rgba(0,0,0,0.08)",
};

export default function TestResultsPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { id: courseId, testId, studentId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [testDef, setTestDef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rosterName, setRosterName] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchClassById(courseId),
      fetchTestSubmission(testId, studentId),
      fetchTestById(testId),
      Promise.resolve(fetchCourseRoster?.(courseId, { peersOnly: true })).catch(() => null),
    ])
      .then(([cls, sub, test, roster]) => {
        setRosterName((Array.isArray(roster) ? roster : []).find((u) => String(u.id) === String(studentId))?.name || null);
        setCourse(cls);
        setTestDef(test);
        setSubmission(sub);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load results.");
        setLoading(false);
      });
  }, [courseId, testId, studentId]);

  // Build rich answer breakdown from raw submission answers + test question definitions
  function buildAnswers(rawAnswers, questions) {
    if (!Array.isArray(rawAnswers) || !Array.isArray(questions)) return rawAnswers || [];
    return rawAnswers.map((a, idx) => {
      const qId = String(a.questionId ?? "");
      const q = questions.find((qu) => String(qu.id) === qId || String(qu.questionId) === qId);
      if (!q) return a;

      const options = Array.isArray(q.options) ? q.options : [];
      const correctOption = options.find((o) => o.isCorrect);

      // Multiple choice / true-false
      if (a.selectedOptionId != null) {
        const selected = options.find((o) => String(o.id) === String(a.selectedOptionId));
        return {
          ...a,
          questionText: q.questionText || q.text || `Question ${idx + 1}`,
          selectedOptionText: selected?.text ?? String(a.selectedOptionId),
          correctOptionText: correctOption?.text ?? null,
          correct: selected?.isCorrect === true,
        };
      }

      // Select-all
      if (Array.isArray(a.selectedOptionIds)) {
        const selectedTexts = a.selectedOptionIds.map((id) => {
          const o = options.find((op) => String(op.id) === String(id));
          return o?.text ?? String(id);
        });
        const correctIds = new Set(options.filter((o) => o.isCorrect).map((o) => String(o.id)));
        const selectedIds = new Set(a.selectedOptionIds.map(String));
        const correct = correctIds.size === selectedIds.size && [...correctIds].every((id) => selectedIds.has(id));
        return {
          ...a,
          questionText: q.questionText || q.text || `Question ${idx + 1}`,
          selectedOptionText: selectedTexts.join(", "),
          correctOptionText: options.filter((o) => o.isCorrect).map((o) => o.text).join(", "),
          correct,
        };
      }

      // Short answer / text
      if (a.value != null) {
        return {
          ...a,
          questionText: q.questionText || q.text || `Question ${idx + 1}`,
          selectedOptionText: a.value,
          correctOptionText: correctOption?.text ?? null,
          correct: a.correct ?? false,
        };
      }

      return {
        ...a,
        questionText: q.questionText || q.text || `Question ${idx + 1}`,
      };
    });
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No submission found for this student.</Alert>
      </Box>
    );
  }

  const rawAnswers = Array.isArray(submission.answers) ? submission.answers : [];
  const questions = Array.isArray(testDef?.questions) ? testDef.questions : [];
  const answers = buildAnswers(rawAnswers, questions);
  const passed = submission.passed;
  const score = submission.score;
  const totalPoints = submission.totalPoints;
  const studentName = submission.studentName || rosterName || `Student ${studentId}`;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3, bgcolor: "#fff", minHeight: "100vh" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={
          <NavigateNextIcon fontSize="small" sx={{ color: "var(--color-ink-4)" }} />
        }
        sx={{ mb: 3 }}
      >
        <Link
          href="#"
          underline="hover"
          sx={{ color: tPrimary }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/course-management/${courseId}/tests`);
          }}
        >
          {course?.title || `Course ${courseId}`}
        </Link>
        <Link
          href="#"
          underline="hover"
          sx={{ color: tPrimary }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/course-management/${courseId}/tests/${testId}/assess`);
          }}
        >
          Test Results
        </Link>
        <Typography
          color="text.primary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <AssessmentIcon fontSize="small" />
          {studentName}
        </Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderColor: DS.border,
          bgcolor: DS.surface,
        }}
      >
        <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: tPrimary, fontFamily: "var(--font-display)" }}
            >
              {score} / {totalPoints ?? "?"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Score
            </Typography>
          </Box>
          <Box>
            <Chip
              label={passed ? "PASSED" : "FAILED"}
              color={passed ? "success" : "error"}
              icon={passed ? <CheckCircleIcon /> : <CancelIcon />}
              sx={{ fontWeight: 700, fontSize: "0.9rem" }}
            />
          </Box>
          {submission.submittedAt && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Submitted
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(submission.submittedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
          {submission.startedAt && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Started
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(submission.startedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Answer breakdown */}
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: tPrimary, fontFamily: "var(--font-display)" }}
      >
        Answer Breakdown
      </Typography>

      {answers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No answer details available.
        </Typography>
      ) : (
        <List disablePadding>
          {answers.map((answer, idx) => {
            const correct = answer.correct;
            return (
              <Paper
                key={answer.questionId ?? idx}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  p: 2,
                  borderColor: correct ? "success.light" : "error.light",
                  bgcolor: correct
                    ? "rgba(46,125,50,0.04)"
                    : "rgba(211,47,47,0.04)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  {correct ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Q{idx + 1}: {answer.questionText || `Question ${idx + 1}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Student answered:{" "}
                      <strong>
                        {answer.selectedOptionText ||
                          answer.selectedOptionId ||
                          "(no answer)"}
                      </strong>
                    </Typography>
                    {!correct && (
                      <Typography
                        variant="body2"
                        sx={{ color: "success.main" }}
                      >
                        Correct answer:{" "}
                        <strong>
                          {answer.correctOptionText ||
                            answer.correctOptionId ||
                            "N/A"}
                        </strong>
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </List>
      )}
    </Box>
  );
}
